export interface ConversationThread {
  id: string;
  slug: string;
  title: string;
  category: string;
  postType: 'Question' | 'Recommendation Request' | 'Discussion' | 'Offer' | 'Alert' | 'Poll';
  authorName: string;
  authorRole: string;
  authorTower: string;
  body: string;
  tags: string[];
  replyCount: number;
  helpfulCount: number;
  isSolved: boolean;
  isPinned: boolean;
  isOwnersOnly: boolean;
  isLocked: boolean;
  isRemoved?: boolean;
  date: string;
  lastActivity: string;
}

export interface ThreadReply {
  id: string;
  threadSlug: string;
  authorName: string;
  authorRole: string;
  authorTower: string;
  body: string;
  helpfulCount: number;
  isHelpful?: boolean;
  date: string;
}

export const mockThreads: ConversationThread[] = [
  {
    id: 't1',
    slug: 'best-plumber-recommendation',
    title: 'Best plumber recommendation for high-rise units?',
    category: 'Recommendations',
    postType: 'Recommendation Request',
    authorName: 'Sofia Reyes',
    authorRole: 'Renter',
    authorTower: 'Tower 2',
    body: 'My kitchen faucet has been dripping for two weeks and I need a plumber who actually knows how to work in high-rise buildings. The last one I called had no idea about the shut-off procedures. Anyone have a reliable recommendation?',
    tags: ['Plumbing', 'Maintenance', 'Recommendations'],
    replyCount: 7,
    helpfulCount: 12,
    isSolved: true,
    isPinned: false,
    isOwnersOnly: false,
    isLocked: false,
    date: 'March 15, 2026',
    lastActivity: '2 hours ago',
  },
  {
    id: 't2',
    slug: 'gym-peak-hours-poll',
    title: 'What time do you use the gym? (Trying to avoid peak hours)',
    category: 'Fitness & Wellness',
    postType: 'Poll',
    authorName: 'Ryan Kowalski',
    authorRole: 'Renter',
    authorTower: 'Tower 1',
    body: 'The gym has been packed lately, especially the squat rack. Curious when everyone goes so we can spread out a bit. Vote below and feel free to share your preferred time.',
    tags: ['Gym', 'Fitness', 'Amenities'],
    replyCount: 14,
    helpfulCount: 8,
    isSolved: false,
    isPinned: false,
    isOwnersOnly: false,
    isLocked: false,
    date: 'March 14, 2026',
    lastActivity: '5 hours ago',
  },
  {
    id: 't3',
    slug: 'rooftop-event-planning',
    title: 'Interested in organizing a rooftop social for residents?',
    category: 'Events & Social',
    postType: 'Discussion',
    authorName: 'Priya Nair',
    authorRole: 'Owner',
    authorTower: 'Tower 1',
    body: 'I\'ve been thinking it would be great to organize a casual rooftop gathering for residents — maybe a Friday evening in April. Nothing formal, just a chance to meet neighbors. Would anyone be interested in helping organize or just attending?',
    tags: ['Events', 'Social', 'Community', 'Rooftop'],
    replyCount: 22,
    helpfulCount: 31,
    isSolved: false,
    isPinned: true,
    isOwnersOnly: false,
    isLocked: false,
    date: 'March 12, 2026',
    lastActivity: '1 day ago',
  },
  {
    id: 't4',
    slug: 'package-theft-alert',
    title: 'Alert: Package theft reported in Tower 2 lobby',
    category: 'General',
    postType: 'Alert',
    authorName: 'Natasha Volkov',
    authorRole: 'Owner',
    authorTower: 'Tower 2',
    body: 'Wanted to flag that two residents have reported packages missing from the Tower 2 lobby area in the past week. Please make sure to collect deliveries promptly and consider using Amazon lockers or requesting signature delivery.',
    tags: ['Security', 'Packages', 'Alert', 'Tower 2'],
    replyCount: 9,
    helpfulCount: 47,
    isSolved: false,
    isPinned: true,
    isOwnersOnly: false,
    isLocked: false,
    date: 'March 11, 2026',
    lastActivity: '3 days ago',
  },
  {
    id: 't5',
    slug: 'owners-parking-allocation',
    title: 'Parking allocation discussion — Tower 1 owners',
    category: 'Owners Only',
    postType: 'Discussion',
    authorName: 'James Whitfield',
    authorRole: 'Multiple Unit Owner',
    authorTower: 'Tower 1',
    body: 'Wanted to start a thread for owners to discuss the current parking allocation situation. Several of us with multiple units have concerns about the current assignment process.',
    tags: ['Parking', 'Owners', 'Tower 1'],
    replyCount: 6,
    helpfulCount: 14,
    isSolved: false,
    isPinned: false,
    isOwnersOnly: true,
    isLocked: false,
    date: 'March 10, 2026',
    lastActivity: '4 days ago',
  },
  {
    id: 't6',
    slug: 'best-coffee-near-panorama',
    title: 'Best coffee spots within walking distance?',
    category: 'Recommendations',
    postType: 'Recommendation Request',
    authorName: 'Amanda Park',
    authorRole: 'Renter',
    authorTower: 'Tower 2',
    body: 'Just moved in last month and still figuring out the neighborhood. What are your go-to coffee spots within a reasonable walk or short drive from the building?',
    tags: ['Coffee', 'Local', 'Recommendations', 'Neighborhood'],
    replyCount: 18,
    helpfulCount: 24,
    isSolved: false,
    isPinned: false,
    isOwnersOnly: false,
    isLocked: false,
    date: 'March 9, 2026',
    lastActivity: '5 days ago',
  },
  {
    id: 't7',
    slug: 'dog-walking-services',
    title: 'Looking for a reliable dog walker in the building',
    category: 'Pets',
    postType: 'Question',
    authorName: 'Maya Johnson',
    authorRole: 'Resident',
    authorTower: 'Tower 1',
    body: 'I have a 2-year-old golden retriever and I\'m looking for someone who can walk him on weekday mornings around 7-8am. Ideally someone already in the building who knows the pet areas. Any recommendations?',
    tags: ['Pets', 'Dog Walking', 'Services'],
    replyCount: 5,
    helpfulCount: 3,
    isSolved: false,
    isPinned: false,
    isOwnersOnly: false,
    isLocked: false,
    date: 'March 8, 2026',
    lastActivity: '6 days ago',
  },
  {
    id: 't8',
    slug: 'fitness-class-interest',
    title: 'Anyone interested in a weekly group fitness class?',
    category: 'Fitness & Wellness',
    postType: 'Discussion',
    authorName: 'Derek Okafor',
    authorRole: 'Resident',
    authorTower: 'Tower 2',
    body: 'I\'m a certified trainer in the building and thinking about organizing a weekly group class in the fitness center — maybe Saturday mornings. Would be free for residents. Gauge of interest?',
    tags: ['Fitness', 'Group Class', 'Community', 'Free'],
    replyCount: 31,
    helpfulCount: 42,
    isSolved: false,
    isPinned: false,
    isOwnersOnly: false,
    isLocked: false,
    date: 'March 7, 2026',
    lastActivity: '1 week ago',
  },
];

export const mockReplies: ThreadReply[] = [
  {
    id: 'rep1',
    threadSlug: 'best-plumber-recommendation',
    authorName: 'Marcus Chen',
    authorRole: 'Owner',
    authorTower: 'Tower 1',
    body: 'I\'ve used Desert Plumbing Solutions twice — they know high-rise work and are familiar with the building shut-off procedures. Ask for Tony specifically. Number is in the services directory.',
    helpfulCount: 8,
    isHelpful: true,
    date: 'March 15, 2026',
  },
  {
    id: 'rep2',
    threadSlug: 'best-plumber-recommendation',
    authorName: 'James Whitfield',
    authorRole: 'Multiple Unit Owner',
    authorTower: 'Tower 1',
    body: 'Second the Desert Plumbing recommendation. Also, for future reference — the building requires 24-hour notice for any work that involves the main water shutoff. Worth knowing before you book.',
    helpfulCount: 6,
    date: 'March 15, 2026',
  },
  {
    id: 'rep3',
    threadSlug: 'best-plumber-recommendation',
    authorName: 'Natasha Volkov',
    authorRole: 'Owner',
    authorTower: 'Tower 2',
    body: 'I had a similar issue last year. Ended up using a plumber from the building\'s preferred vendor list — they were fast and knew the building layout. Worth asking the front desk for that list.',
    helpfulCount: 4,
    date: 'March 15, 2026',
  },
  {
    id: 'rep4',
    threadSlug: 'rooftop-event-planning',
    authorName: 'Marcus Chen',
    authorRole: 'Owner',
    authorTower: 'Tower 1',
    body: 'I\'m in! Happy to help organize. I know a few people who could bring a portable speaker setup. April works well — the weather should be perfect.',
    helpfulCount: 5,
    date: 'March 13, 2026',
  },
  {
    id: 'rep5',
    threadSlug: 'rooftop-event-planning',
    authorName: 'Sofia Reyes',
    authorRole: 'Renter',
    authorTower: 'Tower 2',
    body: 'Would love this! I can help with design/signage if needed. Also know a few residents in Tower 2 who would definitely come.',
    helpfulCount: 3,
    date: 'March 13, 2026',
  },
  {
    id: 'rep6',
    threadSlug: 'rooftop-event-planning',
    authorName: 'Derek Okafor',
    authorRole: 'Resident',
    authorTower: 'Tower 2',
    body: 'Count me in. I can help with setup and breakdown. Would be great to finally meet more neighbors in person.',
    helpfulCount: 2,
    date: 'March 12, 2026',
  },
  {
    id: 'rep7',
    threadSlug: 'fitness-class-interest',
    authorName: 'Priya Nair',
    authorRole: 'Owner',
    authorTower: 'Tower 1',
    body: 'Yes! Saturday mornings would be perfect. I\'d love a mix of strength and cardio if possible. What time were you thinking?',
    helpfulCount: 7,
    isHelpful: true,
    date: 'March 8, 2026',
  },
  {
    id: 'rep8',
    threadSlug: 'fitness-class-interest',
    authorName: 'Maya Johnson',
    authorRole: 'Resident',
    authorTower: 'Tower 1',
    body: 'I\'d be happy to co-lead with a yoga warm-up or cool-down if that would help. Let me know!',
    helpfulCount: 9,
    date: 'March 8, 2026',
  },
];
